import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  forwardRef, useCallback,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { useTheme } from '../../themes/theme.hooks';
import { type ThemeProps } from '../../types/themeProps';

const iconStyle = (props: ThemeProps<{
  baseColor?: string;
  hoverColor?: string;
  size?: number;
}>) => css({
  color: props.baseColor,
  fontSize: props.size ?? 16,

  '&:hover': {
    color: props.hoverColor,
  },
});

type HoverableIconProps = Readonly<{
  baseColor?: string;
  className?: string;
  disabledColor?: string;
  hoverColor: string;
  icon: IconProp;
  isDisabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  size?: number;
}>;

export const HoverableIconComponent: React.FC<HoverableIconProps> = forwardRef<SVGSVGElement, HoverableIconProps>(({
  baseColor,
  className,
  disabledColor,
  hoverColor,
  icon,
  isDisabled,
  onClick,
  size,
  ...restProps
}, ref) => {
  const theme = useTheme();

  const getActiveOrDisabledColor = useCallback((color?: string) => {
    if (isDisabled) {
      return disabledColor || theme.textColors.secondary;
    }

    return color;
  }, [disabledColor, isDisabled, theme.textColors.secondary]);

  return (
    <FontAwesomeIcon
      {...restProps}
      ref={ref}
      className={className}
      css={iconStyle({
        baseColor: getActiveOrDisabledColor(baseColor),
        size,
        hoverColor: getActiveOrDisabledColor(hoverColor),
        theme,
      })}
      icon={icon}
      onClick={onClick}
    />
  );
});

HoverableIconComponent.displayName = 'HoverableIcon';
